/**
 * Agent 流式处理 Hook
 *
 * 职责：
 * 1. 管理 Agent 运行状态（idle/running/connecting/error）
 * 2. 处理流式消息的接收和状态更新
 * 3. 管理 Agent Run ID 和评分 Run ID
 * 4. 自动启动和停止 Agent 流式请求
 * 5. 处理沙箱初始化时的特殊 tool call
 * 6. 发送系统通知（成功/失败）
 * 7. 提供停止 Agent 的功能
 *
 * 核心功能：
 * - 封装 useAgentStream hook，提供更高层的抽象
 * - 自动同步 agentRunId 到 rateAgentRunId
 * - 监听沙箱状态，自动设置初始化 tool call
 * - 当 agentRunId 变化时自动启动流式请求和沙箱轮询
 *
 * 使用场景：
 * - 会话页面需要处理 Agent 流式响应时
 * - 需要管理 Agent 运行状态时
 */
import { useCallback, useEffect, useState } from 'react';
import { useAgentStream } from '@/hooks/useAgentStream';
import { getThreadStatusFromMessage } from '@/lib/thread';
import { useStores } from '@/store/hooks/useStore';
import useThreadSystemNotification from './deep-mining/use-thread-system-notification';
import { toast } from 'sonner';
import { useI18n } from '@/hooks/use-i18n';
import { useCallbackRef } from './use-callback-ref';

interface UseThreadAgentParams {
  threadId: string;
  scrollToBottom: (behavior?: 'auto' | 'smooth') => void;
}

export type AgentStatus = 'idle' | 'running' | 'connecting' | 'error';

interface UseThreadAgentReturn {
  agentStatus: AgentStatus;
  setAgentStatus: (status: AgentStatus) => void;
  agentRunId: string | null;
  rateAgentRunId: string | null;
  setAgentRunId: (id: string | null) => void;
  setRateAgentRunId: (id: string | null) => void;
  isRunning: boolean;
  isStopping: boolean;
  streamHookStatus: string;
  handleStopAgent: () => Promise<void>;
}

export const useThreadAgent = ({
  threadId,
  scrollToBottom,
}: UseThreadAgentParams): UseThreadAgentReturn => {
  const { t } = useI18n('thread');
  const stores = useStores();
  const {
    setThreadStatus,
    resetThreadStatus,
    addMessage,
    removeMessage,
    pendingMessages,
    clearPendingMessages,
  } = stores.threadStore;
  const { blockToolCalls, setCurrentToolCall } = stores.threadToolStore;
  const {
    projectId,
    projectName,
    sandboxStatus,
    startSandboxPolling,
    stopSandboxPolling,
  } = stores.threadProjectStore;

  const [agentRunId, setAgentRunId] = useState<string | null>(null);
  const [rateAgentRunId, setRateAgentRunId] = useState<string | null>(null);
  const [agentStatus, setAgentStatus] = useState<AgentStatus>('idle');
  const [isStopping, setIsStopping] = useState(false);

  // 处理流式消息
  const handleNewMessageFromStream = useCallback(
    (message: MESSAGE.Message) => {
      // 特殊处理积分不足的消息
      if (message.type === 'status') {
        const threadStatus = getThreadStatusFromMessage(message);
        if (threadStatus) {
          const [status, metadata] = threadStatus;
          setThreadStatus(status, metadata);
        }
      }
      addMessage(message);
    },
    [addMessage, setThreadStatus],
  );

  const { sendNotification } = useThreadSystemNotification();
  // 处理流式状态变化
  const handleStreamStatusChange = useCallback(
    (hookStatus: string) => {
      switch (hookStatus) {
        case 'idle':
        case 'completed':
        case 'stopped':
        case 'agent_not_running':
        case 'error':
        case 'failed':
          setAgentStatus('idle');
          setAgentRunId(null);
          // 发送系统通知
          const notificationType =
            hookStatus === 'completed' ? 'SUCCESS' : 'FAIL';
          sendNotification({
            type: notificationType,
            title: stores.threadProjectStore.projectName,
          });

          // 滚动到底部显示最新消息
          if (
            [
              'completed',
              'stopped',
              'agent_not_running',
              'error',
              'failed',
            ].includes(hookStatus)
          ) {
            scrollToBottom('smooth');
          }
          break;
        case 'connecting':
          setAgentStatus('connecting');
          break;
        case 'streaming':
          setAgentStatus('running');
          break;
      }
    },
    [projectName, scrollToBottom],
  );

  // 处理流式错误
  const handleStreamError = useCallback((errorMessage: string) => {
    console.error(`[PAGE] Stream hook error: ${errorMessage}`);
    if (
      !errorMessage.toLowerCase().includes('not found') &&
      !errorMessage.toLowerCase().includes('agent run is not running')
    ) {
      // toast.error(`Stream Error: ${errorMessage}`);
      window.dispatchEvent(
        new CustomEvent('toast-error', {
          detail: { message: `Stream Error: ${errorMessage}` },
        }),
      );
    }
  }, []);

  // 处理流式关闭
  const handleStreamClose = useCallback(() => {
    console.log(`[PAGE] Stream hook closed with final status: ${agentStatus}`);
  }, [agentStatus]);

  // 使用 Agent 流式 hook
  const {
    status: streamHookStatus,
    error: streamError,
    agentRunId: currentHookRunId,
    startStreaming,
    stopStreaming,
  } = useAgentStream({
    callbacks: {
      onMessage: handleNewMessageFromStream,
      onStatusChange: handleStreamStatusChange,
      onError: handleStreamError,
      onClose: handleStreamClose,
      onMessageInterrupt: removeMessage,
    },
    threadId,
  });

  // 沙箱创建时设置初始化 tool call
  useEffect(() => {
    if (sandboxStatus === 'CREATING' && !blockToolCalls?.length) {
      setCurrentToolCall({
        name: 'initializing',
        args: {},
        id: '',
        status: 'success',
        artifact: {
          attachments: [],
        },
      });
    }
  }, [sandboxStatus, blockToolCalls]);

  // 当 agentRunId 变化时，启动流式请求
  useEffect(() => {
    if (agentRunId && agentRunId !== currentHookRunId) {
      console.log(
        `[PAGE] Target agentRunId set to ${agentRunId}, initiating stream...`,
      );
      console.log('[STREAM] 开始流式请求', agentRunId);
      startStreaming(agentRunId);
      startSandboxPolling(projectId);
    }
  }, [agentRunId, currentHookRunId, projectId]);

  // 同步 agentRunId 到 rateAgentRunId
  useEffect(() => {
    if (agentRunId) {
      setRateAgentRunId(agentRunId);
    }
  }, [agentRunId]);

  // 停止 Agent
  const handleStopAgent = useCallbackRef(async () => {
    setThreadStatus('STOPPED');
    setIsStopping(true);

    try {
      // 停止沙盒轮询
      stopSandboxPolling();

      // 关键：传入 runId，避免「刚创建 message，但 SSE 还没 startStreaming」导致 stopStreaming 直接 return
      const ok = await stopStreaming(agentRunId || undefined);
      if (ok) {
        return;
      }

      console.error('停止 Agent 失败: stopStreaming 返回 false');
      /**
       * 暂停失败回退策略（用于二次发送）：
       * - 二次发送会先写入 pendingMessages[0].phase='stopping' 并触发 stopStreaming 作为"暂停"
       * - 如果 stopStreaming 失败，说明我们实际上没有暂停成功，应回退到"仍在运行"的状态
       *   也就是：清空 pendingMessages，清空 STOPPED 状态（像根本没暂停过一样）
       */
      // 注意：这里必须读取"最新的 store 状态"，不能用 render 时解构出来的 pendingMessages（会变成闭包旧值）
      const latestPendingMessages = stores.threadStore.pendingMessages;
      const isSecondSendStopping =
        latestPendingMessages[0]?.phase === 'stopping';
      if (isSecondSendStopping) {
        stores.threadStore.clearPendingMessages();
        resetThreadStatus();
        toast.error(t('errors.pauseFailed'));
      } else {
        // 用户手动点击停止也失败：同样回退，不要留 STOPPED 假状态
        resetThreadStatus();
        toast.error(t('errors.stopFailed'));
      }
    } finally {
      setIsStopping(false);
    }
  });

  // 计算是否正在运行
  const isRunning =
    streamHookStatus === 'streaming' || streamHookStatus === 'connecting';

  return {
    agentStatus,
    setAgentStatus,
    agentRunId,
    rateAgentRunId,
    setAgentRunId,
    setRateAgentRunId,
    isRunning,
    isStopping,
    streamHookStatus,
    handleStopAgent,
  };
};
